/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Recycle, Trash, QrCode, ShieldCheck, ArrowRight, ClipboardText,
  Package, Truck, MapPin, Certificate, CalendarCheck, Buildings,
  Warning, House, HandsClapping, Leaf,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

const SERVICES = [
  { Icon: Trash,       title: "Biomedical waste",    desc: "Colour-coded, CPCB-compliant pickup for used sanitary items, sharps and diapers." },
  { Icon: Recycle,     title: "Segregation at source", desc: "Colour-coded bags delivered to your door, with a simple in-app QR to book pickup." },
  { Icon: Package,     title: "Sanitary bin service", desc: "Discreet, sealed collection for feminine hygiene, adult diapers and post-op waste." },
  { Icon: Warning,     title: "Sharps & syringes",   desc: "Puncture-proof containers for insulin needles and lancets. Free swap on collection." },
  { Icon: Certificate, title: "Digital certificates", desc: "Every pickup becomes an e-certificate — verifiable, printable, audit-ready." },
  { Icon: House,       title: "Home & clinic",       desc: "Households, doctors' clinics, dental studios, salons — one contract, one app." },
  { Icon: Buildings,   title: "Bulk & institutional", desc: "Schools, offices, apartments — multi-point pickup, monthly invoices, LSGD-ready reports." },
  { Icon: ClipboardText,title:"Compliance reports",  desc: "State pollution board filings and CPCB Form V outputs generated for you." },
];

const WHY_US = [
  "CPCB-authorised handling — from bag to disposal",
  "Discreet, sealed pickup for sensitive items",
  "QR-tracked chain of custody, end-to-end",
  "Digital certificates emailed after every pickup",
  "Kerala-wide network — homes, clinics, institutions",
  "Zero touch scheduling — book in 20 seconds",
];

export default function HygieneLanding() {
  useEffect(() => {
    document.title = "Hygiene & Waste Services · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="page-hygiene">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-14 md:pb-24 overflow-hidden bg-gradient-to-b from-[#F0F5EE] via-[#EFF6EC]/50 to-white">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 20% 40%, rgba(45,90,39,0.10) 0%, transparent 55%)",
        }}/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-6 lg:px-12 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <Eyebrow color="#2D5A27">Hygiene · Waste · Sanitation</Eyebrow>
            <h1 className="font-heading text-[36px] leading-[1.08] sm:text-5xl md:text-6xl mt-4 tracking-tight text-slate-900">
              Handling waste, so you can <span className="text-[#2D5A27] italic">focus on life</span>.
            </h1>
            <p className="text-base md:text-lg text-slate-600 mt-5 md:mt-6 leading-relaxed max-w-xl">
              Home biomedical waste, sanitary hygiene, sharps and diapers — collected
              discreetly, disposed responsibly and audited digitally. Fully
              CPCB-compliant.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hyg-cta-register" className={BTN_GREEN}>
                Book a pickup <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/contact" data-testid="hyg-cta-contact"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                Institutional plans
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3 md:gap-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={13} weight="fill" className="text-[#2D5A27]"/>CPCB authorised</span>
              <span className="hidden md:inline">·</span>
              <span>BMW Rules 2016</span>
              <span className="hidden md:inline">·</span>
              <span>Kerala-wide</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                       transition={{ duration: 0.7 }}
                       className="relative order-first md:order-last">
            <div className="bg-white rounded-2xl md:rounded-[28px] shadow-2xl ring-1 ring-slate-200 p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Chain of custody · Live</div>
                  <div className="font-heading text-lg text-slate-900">RSG-BAG-98234</div>
                </div>
                <div className="text-[10px] px-2 py-1 bg-[#D4E0D1] text-[#1A3317] rounded font-semibold uppercase tracking-[0.15em]">
                  Authorised
                </div>
              </div>
              <div className="space-y-2.5">
                <TimelineRow color="#2D5A27" Icon={QrCode}   title="Bag scanned"       time="09:12 AM" done/>
                <TimelineRow color="#2D5A27" Icon={Truck}    title="Picked up"         time="09:41 AM" done/>
                <TimelineRow color="#2D5A27" Icon={MapPin}   title="En route to CTF"   time="10:24 AM" done/>
                <TimelineRow color="#0A2540" Icon={ShieldCheck} title="Weighed & incinerated" time="—" pending/>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 py-14 md:py-24">
        <div className="max-w-3xl">
          <Eyebrow color="#2D5A27">What we handle</Eyebrow>
          <h2 className="font-heading text-3xl md:text-5xl mt-4 leading-tight text-slate-900">
            One provider. Every waste stream. Every space.
          </h2>
          <p className="text-slate-600 mt-4 md:mt-5 leading-relaxed">
            Household biomedical waste, sanitary hygiene, sharps and institutional
            volumes — all handled by trained crews with tamper-proof QR tracking.
          </p>
        </div>
        <div className="mt-8 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <FeatureCard key={i} Icon={s.Icon} title={s.title} desc={s.desc} accent="#2D5A27"/>
          ))}
        </div>
      </section>

      {/* WHY-US strip */}
      <section className="bg-[#EFF6EC] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow color="#2D5A27">Why Rescigo</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl mt-3 leading-tight text-slate-900">
              Trusted by families and inspected by regulators.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              We built the only CPCB-authorised network in Kerala that treats a
              household pickup with the same rigour as a hospital contract.
            </p>
          </div>
          <BulletList items={WHY_US} color="#2D5A27"/>
        </div>
      </section>

      {/* HOW-IT-WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 py-14 md:py-24">
        <Eyebrow color="#2D5A27">How it works</Eyebrow>
        <h2 className="font-heading text-3xl md:text-4xl mt-3 leading-tight text-slate-900">
          Book in 20 seconds. Forget about it after.
        </h2>
        <div className="mt-8 md:mt-12 grid sm:grid-cols-3 gap-4 md:gap-6">
          <Step n="01" Icon={CalendarCheck} title="Pick a slot"
                desc="Choose a day + time window. We deliver a sealed bag if you need one."/>
          <Step n="02" Icon={Truck}         title="We collect"
                desc="Trained crew, sealed transport, live QR tracking end-to-end."/>
          <Step n="03" Icon={Certificate}   title="Get your certificate"
                desc="Digital certificate in your inbox — verifiable and audit-ready."/>
        </div>
      </section>

      <CtaBanner
        eyebrow="Rescigo Hygiene"
        title="Handled right, from your home to responsible disposal."
        body="Book a pickup or ask for an institutional quote. It takes less than a minute."
        primary={{ label: "Book a pickup", to: "/register", testid: "hyg-cta-primary" }}
        secondary={{ label: "Talk to us", to: "/contact", testid: "hyg-cta-secondary" }}
        accent="#2D5A27"
      />

      <MarketingFooter/>
    </div>
  );
}

function TimelineRow({ Icon, color, title, time, done, pending }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-2.5 ${done ? "border-[#2D5A27]/25 bg-[#EFF6EC]" : "border-slate-100 bg-white"}`}>
      <div className="w-9 h-9 rounded-lg grid place-items-center flex-none"
           style={{ background: done ? "#D4E0D1" : "#F1F5F9", color }}>
        <Icon size={16} weight="fill"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] text-slate-900 truncate">{title}</div>
        <div className="text-[11px] text-slate-500">{time}</div>
      </div>
      {pending && <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400">pending</span>}
      {done && <HandsClapping size={14} weight="fill" className="text-[#2D5A27]"/>}
    </div>
  );
}

function Step({ n, Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4E0D1] text-[#1A3317]">
          <Icon size={18} weight="duotone"/>
        </div>
        <div className="font-mono text-[11px] text-slate-400 tracking-[0.2em]">{n}</div>
      </div>
      <h3 className="font-heading text-lg text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
      {Icon === Certificate && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] flex items-center gap-1">
          <Leaf size={10} weight="fill"/> Impact tracked
        </div>
      )}
    </div>
  );
}
